import {
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  SkeletonText,
  StatusPill,
  useConfirm,
  useToast,
} from '@aurora/ui-kit';
import type { PillColor } from '@aurora/ui-kit';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { api } from '../api/client.js';

interface TagDto {
  id: string;
  name: string;
  color: PillColor;
  usage: number;
}

const COLOR_OPTIONS: ReadonlyArray<{ value: PillColor; label: string }> = [
  { value: 'gray', label: 'Grau' },
  { value: 'blue', label: 'Blau' },
  { value: 'green', label: 'Grün' },
  { value: 'yellow', label: 'Gelb' },
  { value: 'orange', label: 'Orange' },
  { value: 'red', label: 'Rot' },
  { value: 'purple', label: 'Lila' },
  { value: 'teal', label: 'Türkis' },
  { value: 'pink', label: 'Pink' },
];

/**
 * v4: Tag-Palette pflegen — anlegen, umbenennen, umfärben, löschen.
 * Zuordnen passiert direkt an den Datensätzen (TagsPanel); hier geht es
 * nur um die zentrale Verwaltung der Tags eines Mandanten.
 */
export default function TagsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const confirmDialog = useConfirm();
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState<PillColor>('blue');

  const tags = useQuery({
    queryKey: ['tags', 'all', 'usage'],
    queryFn: () => api<{ items: TagDto[] }>('/api/v1/tags'),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['tags'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api('/api/v1/tags', { method: 'POST', body: { name: newName.trim(), color: newColor } }),
    onSuccess: () => {
      setNewName('');
      toast.success('Tag angelegt');
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const update = useMutation({
    mutationFn: (input: { id: string; name?: string; color?: PillColor }) =>
      api(`/api/v1/tags/${input.id}`, {
        method: 'PUT',
        body: {
          ...(input.name !== undefined ? { name: input.name } : {}),
          ...(input.color !== undefined ? { color: input.color } : {}),
        },
      }),
    onSuccess: refresh,
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/v1/tags/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Tag gelöscht');
      refresh();
    },
    onError: (error) => toast.error(error.message),
  });

  const items = tags.data?.items ?? [];

  return (
    <div className="page">
      <h1 className="page__title">Tags</h1>
      <p className="page__lead">
        Zentrale Tag-Palette deines Mandanten. Tags hängst du direkt an Deals, Firmen, Kontakten und
        Aufgaben an — hier verwaltest du Namen, Farben und räumst auf.
      </p>

      <Card>
        <form
          className="tags-add"
          onSubmit={(event) => {
            event.preventDefault();
            if (newName.trim() !== '') create.mutate();
          }}
        >
          <Input
            placeholder="Neue Tag, z. B. „VIP-Kunde“"
            aria-label="Name der neuen Tag"
            value={newName}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Select
            aria-label="Farbe der neuen Tag"
            value={newColor}
            style={{ width: 120 }}
            onChange={(event) => setNewColor(event.target.value as PillColor)}
          >
            {COLOR_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <Button type="submit" loading={create.isPending}>
            Anlegen
          </Button>
        </form>
      </Card>

      <Card>
        {tags.isLoading ? (
          <SkeletonText lines={4} />
        ) : items.length === 0 ? (
          <EmptyState
            title="Noch keine Tags"
            description="Lege oben deine erste Tag an — oder erstelle sie direkt an einem Datensatz."
          />
        ) : (
          <ul className="tags-list">
            {items.map((tag) => (
              <li key={tag.id} className="tags-list__row">
                <StatusPill color={tag.color}>{tag.name}</StatusPill>
                <Input
                  aria-label={`Name für ${tag.name}`}
                  defaultValue={tag.name}
                  onBlur={(event) => {
                    const name = event.target.value.trim();
                    if (name !== '' && name !== tag.name) update.mutate({ id: tag.id, name });
                  }}
                />
                <Select
                  aria-label={`Farbe für ${tag.name}`}
                  value={tag.color}
                  style={{ width: 120 }}
                  onChange={(event) =>
                    update.mutate({ id: tag.id, color: event.target.value as PillColor })
                  }
                >
                  {COLOR_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
                <span className="tags-list__usage">
                  {tag.usage} {tag.usage === 1 ? 'Verwendung' : 'Verwendungen'}
                </span>
                <button
                  type="button"
                  className="tags-list__delete"
                  aria-label={`Tag ${tag.name} löschen`}
                  onClick={() => {
                    void confirmDialog({
                      title: `Tag „${tag.name}" löschen?`,
                      description:
                        tag.usage > 0
                          ? `Die Tag wird von ${tag.usage} Datensätzen entfernt.`
                          : 'Die Tag wird entfernt.',
                      confirmLabel: 'Löschen',
                      danger: true,
                    }).then((ok) => {
                      if (ok) remove.mutate(tag.id);
                    });
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
